import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_MAX_SPEED_PER_SECOND,
  edgeScrollDelta,
  type ScrollRoom,
} from '@/lib/edgeScroll';

/**
 * Where a card was let go. The board turns this into a placement.
 *
 * `technicianId` is null for the unassigned rail. `slotMinute` is set only by
 * the day view, which has time slots to land in; the week view supplies
 * `isoDate` instead and keeps the job's existing time of day.
 */
export interface DropTarget {
  technicianId: string | null;
  slotMinute?: number;
  isoDate?: string;
}

export interface DragState {
  cardId: string;
  /** Viewport coordinates, for drawing the card that follows the pointer. */
  x: number;
  y: number;
}

/**
 * Dragging a job card, on Pointer Events (M1-11).
 *
 * <b>Pointer Events, not HTML5 drag-and-drop.</b> The AC asks for a trackpad
 * and a touch screen, and `dragstart`/`drop` simply do not fire for touch —
 * the HTML5 API would have shipped a board that works on a desk and not in a
 * van. Pointer Events are one code path for mouse, trackpad, pen and finger.
 *
 * <b>And not a drag-and-drop library.</b> The drop targets here are grid cells
 * this code already owns and can label, the keyboard path is a purpose-built
 * dialog rather than an arrow-key emulation of dragging (see
 * `MoveJobDialog` for why), and what remains is small enough to read in one
 * sitting.
 *
 * Three details that are load-bearing rather than incidental:
 *
 * - A drag only begins after the pointer has moved past a threshold, so a tap
 *   or click stays a tap or click and still opens the dialog. Without it,
 *   every activation of a card would be a zero-distance drag.
 * - Pointer capture routes the rest of the gesture to the card even when the
 *   pointer leaves it, which is most of the gesture. Without capture, dragging
 *   fast enough to outrun the card ends the drag mid-air.
 * - `touch-action: none` on the card (applied by the caller) stops the browser
 *   claiming the gesture for scrolling before this code sees it. On a touch
 *   screen this is the difference between dragging a job and scrolling the
 *   board.
 * - Holding the pointer near an edge scrolls whatever is under it, so a
 *   destination off the screen is still reachable. `elementFromPoint` is
 *   viewport-based, so without this a board 4,000px wide across 25 technicians
 *   could only be dragged between cells that happened to be visible together.
 */
export function useJobDrag({
  onDrop,
  onActivate,
}: {
  onDrop: (cardId: string, target: DropTarget) => void;
  /** A press that never became a drag — a click, a tap, or a keyboard Enter. */
  onActivate: (cardId: string) => void;
}) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const gesture = useRef<{ cardId: string; startX: number; startY: number; dragging: boolean } | null>(
    null,
  );
  // Set when a gesture ended as a real drag, so the `click` the browser fires
  // straight afterwards can be swallowed instead of also opening the dialog.
  //
  // ⚠️ Cleared at pointerDOWN, not only when a click consumes it. A drop does
  // not always produce a click — release the pointer over a different element
  // and the browser may fire none at all — and a flag left standing swallows
  // the NEXT genuine click instead, so the card silently refuses to open its
  // dialog once. Clearing it as each gesture begins means the only click this
  // can suppress is the one belonging to the gesture that just ended. Found by
  // dragging and then clicking in a real browser; jsdom fires exactly the
  // events a test hands it, so it never showed there.
  const justDragged = useRef(false);
  // Where the pointer is right now, for the auto-scroll ticker to read. A ref
  // rather than the `drag` state: the ticker is set up once per drag and would
  // otherwise close over the position it started at.
  const pointer = useRef({ x: 0, y: 0 });
  const isDragging = drag !== null;

  const onPointerDown = useCallback((event: React.PointerEvent, cardId: string) => {
    pointer.current = { x: event.clientX, y: event.clientY };
    // Primary button (or any touch/pen contact) only: a right-click is a
    // context menu, and a middle-click is not a drag.
    if (event.button !== 0) {
      return;
    }
    justDragged.current = false;
    gesture.current = { cardId, startX: event.clientX, startY: event.clientY, dragging: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event: React.PointerEvent) => {
    const current = gesture.current;
    if (!current) {
      return;
    }
    if (!current.dragging) {
      const moved = Math.hypot(event.clientX - current.startX, event.clientY - current.startY);
      // Six pixels: past a shaky finger and a trackpad's click wobble, well
      // short of a deliberate move.
      if (moved < 6) {
        return;
      }
      current.dragging = true;
    }
    pointer.current = { x: event.clientX, y: event.clientY };
    setDrag({ cardId: current.cardId, x: event.clientX, y: event.clientY });
  }, []);

  /**
   * Scroll whatever is under the pointer while it rests near an edge.
   *
   * A timer rather than `requestAnimationFrame`. rAF is the usual choice and
   * is fractionally smoother, but it does not fire at all when the page is not
   * being composited — which is every headless and hidden-pane run, so the
   * behaviour could not be verified anywhere. At this cadence the difference
   * is imperceptible and the step is computed from the interval either way, so
   * the testable option wins. It only ever runs between pointerdown-with-
   * movement and pointerup, and is torn down by the effect's own cleanup.
   */
  useEffect(() => {
    if (!isDragging) {
      return;
    }
    const maxStep = (DEFAULT_MAX_SPEED_PER_SECOND * TICK_MS) / 1000;
    const timer = setInterval(() => {
      autoScrollStep(pointer.current.x, pointer.current.y, maxStep);
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [isDragging]);

  const onPointerUp = useCallback(
    (event: React.PointerEvent) => {
      const current = gesture.current;
      gesture.current = null;
      setDrag(null);
      if (!current) {
        return;
      }
      if (!current.dragging) {
        // A press that never moved. Activation is left to the `click` that
        // follows, so that a mouse and a keyboard take the same path.
        return;
      }
      justDragged.current = true;
      const target = dropTargetAt(event.clientX, event.clientY);
      if (target) {
        onDrop(current.cardId, target);
      }
    },
    [onDrop],
  );

  /**
   * Activation, for every input device at once.
   *
   * `click` rather than `pointerup` because a keyboard Enter or Space on a
   * `<button>` fires `click` and NO pointer events — routing activation
   * through pointerup left the keyboard path dead while looking entirely
   * correct, which is exactly what the AC's "keyboard-accessible alternative"
   * is there to prevent.
   */
  const onClick = useCallback(
    (cardId: string) => () => {
      if (justDragged.current) {
        justDragged.current = false;
        return;
      }
      onActivate(cardId);
    },
    [onActivate],
  );

  const onPointerCancel = useCallback(() => {
    gesture.current = null;
    setDrag(null);
  }, []);

  return {
    drag,
    handlersFor: (cardId: string) => ({
      onPointerDown: (event: React.PointerEvent) => onPointerDown(event, cardId),
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onClick: onClick(cardId),
    }),
  };
}

/**
 * What is under the pointer, read from the DOM rather than tracked in React
 * state.
 *
 * The alternative — every cell registering itself and the drag hook doing its
 * own hit testing — reimplements what the browser already does perfectly, and
 * gets it wrong the moment a cell scrolls. Reading it back at drop time is one
 * call and cannot go stale.
 *
 * The card following the pointer must be `pointer-events: none`, or this finds
 * the card instead of the cell beneath it.
 */
function dropTargetAt(x: number, y: number): DropTarget | null {
  const element = document.elementFromPoint(x, y);
  const cell = element?.closest<HTMLElement>('[data-drop-target]');
  if (!cell) {
    return null;
  }

  const technicianId = cell.dataset.dropTechnician ?? null;
  const isoDate = cell.dataset.dropDate;

  // The day view: turn the vertical position inside the column into a slot.
  if (cell.dataset.dropSlotHeight && cell.dataset.dropStartMinute) {
    const slotHeight = Number(cell.dataset.dropSlotHeight);
    const startMinute = Number(cell.dataset.dropStartMinute);
    const slotMinutes = Number(cell.dataset.dropSlotMinutes);
    const offsetY = y - cell.getBoundingClientRect().top;
    const slotIndex = Math.max(0, Math.floor(offsetY / slotHeight));
    return { technicianId, slotMinute: startMinute + slotIndex * slotMinutes, isoDate };
  }

  return { technicianId, isoDate };
}

/** ~60 ticks a second, matching a display refresh without depending on one. */
const TICK_MS = 16;

/**
 * Scrolls the innermost container under the pointer that can move the way the
 * pointer is leaning, and the page for whichever axis no container claimed.
 *
 * Innermost first, one container per AXIS: without that, holding the pointer
 * at the right edge scrolls the board AND the page together, at which point
 * nothing lands where it looks like it should. The board wins the axis it can
 * move, and the page picks up the one it cannot — which is exactly the week
 * view, where the columns scroll sideways inside the board while the
 * technician rows run down the page.
 */
function autoScrollStep(x: number, y: number, maxStep: number): void {
  let handledX = false;
  let handledY = false;

  for (const target of scrollChain(x, y)) {
    if (handledX && handledY) {
      return;
    }
    const { dx, dy } = edgeScrollDelta(target.rect, { x, y }, target.room, { maxStep });

    if (!handledX && dx !== 0) {
      target.scrollBy(dx, 0);
      handledX = true;
    }
    if (!handledY && dy !== 0) {
      target.scrollBy(0, dy);
      handledY = true;
    }
  }
}

interface ScrollTarget {
  rect: { left: number; top: number; right: number; bottom: number };
  room: ScrollRoom;
  scrollBy: (dx: number, dy: number) => void;
}

/**
 * The scrollable things under the pointer, innermost first, with the page
 * last.
 *
 * Found by walking up from `elementFromPoint` rather than from a list of known
 * selectors: the day board, the week board and the unassigned rail are all
 * scrollers, and a fourth would otherwise have to remember to register itself.
 */
function* scrollChain(x: number, y: number): Generator<ScrollTarget> {
  let element = document.elementFromPoint(x, y);

  while (element && element !== document.body && element !== document.documentElement) {
    if (element instanceof HTMLElement && isScrollable(element)) {
      const rect = element.getBoundingClientRect();
      yield {
        rect,
        room: roomIn(element),
        scrollBy: (dx, dy) => {
          element!.scrollLeft += dx;
          element!.scrollTop += dy;
        },
      };
    }
    element = element.parentElement;
  }

  // The page itself. Its "rect" is the viewport, not the document, because the
  // pointer coordinates are viewport coordinates.
  const scroller = document.scrollingElement ?? document.documentElement;
  yield {
    rect: {
      left: 0,
      top: 0,
      right: document.documentElement.clientWidth,
      bottom: document.documentElement.clientHeight,
    },
    room: roomIn(scroller),
    scrollBy: (dx, dy) => {
      scroller.scrollLeft += dx;
      scroller.scrollTop += dy;
    },
  };
}

function isScrollable(element: HTMLElement): boolean {
  const style = getComputedStyle(element);
  const scrolls = (value: string) => value === 'auto' || value === 'scroll';
  return (
    (scrolls(style.overflowX) && element.scrollWidth > element.clientWidth) ||
    (scrolls(style.overflowY) && element.scrollHeight > element.clientHeight)
  );
}

function roomIn(element: Element): ScrollRoom {
  return {
    left: element.scrollLeft,
    right: element.scrollWidth - element.clientWidth - element.scrollLeft,
    up: element.scrollTop,
    down: element.scrollHeight - element.clientHeight - element.scrollTop,
  };
}
